// Job-record rules every view shares (DESIGN_SPEC §12.17).
//   jobName(job)            the run's display name: the file name, or host + path for a URL run (no scheme), else the
//                           sample's short hash or the id; one name in lists, headlines, titles, nodes and recent runs
//   urlScheme(job)          "http" | "https" | "url" for a URL run (the list's tag), else null
//   pastTimeout(job, now)   dispatched longer ago than its timeout plus a minute
//   liveness(state, job, now) → "live" | "overdue" | "queued" | null   the one rule behind every "live" word and count:
//                           on a node within its timeout (live), on a node past it (overdue: the record lags or the node is
//                           stuck), waiting for a slot (queued), or over. The Analyses chip, rows and lede, the status bar,
//                           the nav mark and the report header all read it.
//   recordLiveness(job, now) · liveRecord(job, now)   the same from the record alone (the shell has no stream)
import type { Job } from "../types";

const RECORD_GRACE_MS = 60_000;

export type Liveness = "live" | "overdue" | "queued" | null;

const ON_NODE: ReadonlySet<string> = new Set(["dispatched", "provisioning", "booting", "running", "collecting"]);

const short = (s: string) => (s.length > 17 ? `${s.slice(0, 8)}…` : s);

export function jobName(j: Pick<Job, "kind" | "url" | "filename" | "sample_sha" | "id">): string {
  if (j.kind === "url" && j.url) {
    try {
      const u = new URL(j.url);
      const path = u.pathname.replace(/\/+$/, "");
      return `${u.host}${path}`;
    } catch {
      return j.url.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/\/+$/, "");
    }
  }
  return j.filename || (j.sample_sha ? short(j.sample_sha) : "") || short(j.id);
}

export function urlScheme(j: Pick<Job, "kind" | "url">): "http" | "https" | "url" | null {
  if (j.kind !== "url") return null;
  const m = /^(https?):/i.exec(j.url ?? "");
  return m ? (m[1].toLowerCase() as "http" | "https") : "url";
}

/** Dispatched longer ago than the run's timeout plus a minute. */
export function pastTimeout(j: Pick<Job, "dispatched_at" | "timeout_secs">, now: number): boolean {
  const start = Date.parse(j.dispatched_at ?? "");
  return Number.isFinite(start) && now - start > Math.max(0, Number(j.timeout_secs) || 0) * 1000 + RECORD_GRACE_MS;
}

/** Where a run in `state` stands for every "live" count and badge. */
export function liveness(state: string, j: Pick<Job, "dispatched_at" | "timeout_secs">, now: number): Liveness {
  if (state === "queued") return "queued";
  if (!ON_NODE.has(state)) return null;
  return pastTimeout(j, now) ? "overdue" : "live";
}

export function recordLiveness(j: Pick<Job, "state" | "dispatched_at" | "timeout_secs">, now: number): Liveness {
  return liveness(j.state, j, now);
}

/** The record says the run is on a node, within its timeout. */
export function liveRecord(j: Pick<Job, "state" | "dispatched_at" | "timeout_secs">, now: number): boolean {
  return recordLiveness(j, now) === "live";
}
